import mapboxgl from "mapbox-gl";
import { parseGeoData } from "senf-shared";
import { flyToPolygon } from "../utils/flyToPolygon";

const useIdeaPin = (
  statefulMap,
  container,
  pinData,
  setIdeaMarkerColor,
  paddings,
  setGisData,
  normalizedGisData,
) => {
  const mapboxMarker = new mapboxgl.Marker({ element: container });

  // Function to set selected layer visibility
  const setSelectedLayer = (map: mapboxgl.Map | undefined, ideaId?: string) => {
    if (!map) return;

    if (!ideaId) {
      // Hide all selected layers when no feature is selected
      [
        "Point",
        "LineString",
        "MultiLineString",
        "Polygon",
        "MultiPolygon",
      ].forEach((type) => {
        const selectedLayerId = `gisData-${type}-selected`;
        if (map?.getLayer(selectedLayerId)) {
          map.setLayoutProperty(selectedLayerId, "visibility", "none");
        }
      });
      return;
    }

    // Show and update selected layers
    [
      "Point",
      "LineString",
      "MultiLineString",
      "Polygon",
      "MultiPolygon",
    ].forEach((type) => {
      const selectedLayerId = `gisData-${type}-selected`;
      if (map?.getLayer(selectedLayerId)) {
        map.setLayoutProperty(selectedLayerId, "visibility", "visible");
        map.setFilter(selectedLayerId, ["==", ["get", "ideaId"], ideaId]);
      }
    });
  };

  // Function to set layer opacity
  const setLayerOpacity = (map: mapboxgl.Map | undefined, opacity: number) => {
    if (!map) return;

    // Points
    if (map.getLayer("gisData-Point")) {
      map.setPaintProperty("gisData-Point", "circle-opacity", opacity);
      map.setPaintProperty("gisData-Point", "circle-stroke-opacity", opacity);
    }

    // Lines
    if (map.getLayer("gisData-LineString")) {
      map.setPaintProperty("gisData-LineString", "line-opacity", opacity);
    }

    if (map.getLayer("gisData-MultiLineString")) {
      map.setPaintProperty("gisData-MultiLineString", "line-opacity", opacity);
    }

    // Polygons
    if (map.getLayer("gisData-Polygon")) {
      map.setPaintProperty("gisData-Polygon", "fill-opacity", opacity);
    }

    if (map.getLayer("gisData-MultiPolygon")) {
      map.setPaintProperty("gisData-MultiPolygon", "fill-opacity", opacity);
    }

    // Additional polygon layers that might exist
    if (map.getLayer("gisData-Polygon-outline")) {
      map.setPaintProperty("gisData-Polygon-outline", "line-opacity", opacity);
    }

    if (map.getLayer("gisData-MultiPolygon-outline")) {
      map.setPaintProperty(
        "gisData-MultiPolygon-outline",
        "line-opacity",
        opacity,
      );
    }

    if (map.getLayer("gisData-Polygon-outline-selected")) {
      map.setPaintProperty(
        "gisData-Polygon-outline-selected",
        "line-opacity",
        opacity,
      );
    }

    if (map.getLayer("gisData-MultiPolygon-outline-selected")) {
      map.setPaintProperty(
        "gisData-MultiPolygon-outline-selected",
        "line-opacity",
        opacity,
      );
    }
  };

  if (pinData?.geoData && statefulMap) {
    const parsedGeoData = parseGeoData(pinData.geoData);
    setGisData([...normalizedGisData, pinData.geoData]);
    flyToPolygon(statefulMap, parsedGeoData, { duration: 1000 });

    setSelectedLayer(statefulMap, pinData.ideaId);

    setTimeout(() => {
      setLayerOpacity(statefulMap, 0.3);
    }, 100);
  } else {
    mapboxMarker.remove();

    if (statefulMap) {
      setSelectedLayer(statefulMap);
      setLayerOpacity(statefulMap, 1);
    }
  }

  if (pinData?.long && statefulMap?._loaded) {
    const ideasLayer = statefulMap?.getLayer("gisData-Point");
    if (ideasLayer) {
      // Only filter out the specific idea point, show all other points
      statefulMap?.setFilter("gisData-Point", [
        "all",
        [
          "any",
          ["!", ["has", "ideaId"]], // Show points without ideaId
          ["!=", ["get", "ideaId"], pinData.ideaId], // Or points with different ideaId
        ],
        ["==", ["geometry-type"], "Point"], // Still exclude vertices
      ]);
    }
    mapboxMarker.setLngLat([pinData.long, pinData.lat]).addTo(statefulMap);
    setIdeaMarkerColor(pinData.color);
    statefulMap.flyTo({
      center: [pinData.long, pinData.lat],
      zoom: 16.5,
      speed: 1,
      pitch: 30,
      padding: {
        top: 0,
        bottom: paddings?.bottom || 0,
        left: paddings?.left || 0,
        right: 0,
      },
    });
    setLayerOpacity(statefulMap, 0.3);
  } else {
    mapboxMarker.remove();
    if (statefulMap) {
      // Reset filter to show all points
      statefulMap?.setFilter("gisData-Point", [
        "==",
        ["geometry-type"],
        "Point", // Only filter out vertices
      ]);
      setLayerOpacity(statefulMap, 1);
    }

    setTimeout(() => {
      if (statefulMap?._loaded) {
        // const ideasLayer = statefulMap?.getLayer("gisData-Point");
        // if (ideasLayer) {
        //   statefulMap.setFilter("gisData-Point", null);
        // }
      }
    }, 100);
  }
};

export default useIdeaPin;
